// Manages channel selection (sms/email) on the two-step send-message form step 2.
//
// Usage:
// <div data-controller="send-message-steps">
//   <div data-send-message-steps-target="smsCard" ...>SMS</div>
//   <div data-send-message-steps-target="emailCard" ...>Email</div>
// </div>

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["phoneField", "emailField", "smsConsent", "emailConsent", "smsCard", "emailCard", "phoneTrust", "emailTrust", "smsConsentCheckbox", "smsConsentError"]
  static values = {
    channel: { type: String, default: "sms" },
  }

  connect() {
    this.updateChannelUI()
  }

  selectChannel(event) {
    this.channelValue = event.currentTarget.dataset.channel
  }

  smsConsentChanged() {
    this.updateSmsConsentUI()
  }

  attemptSubmit(event) {
    const btn = event.currentTarget.querySelector("button[type='submit']")
    if (btn && btn.disabled) {
      event.preventDefault()
      if (this.hasSmsConsentErrorTarget) this.smsConsentErrorTarget.classList.remove("hidden")
    }
  }

  goBack(event) {
    const btn = event.currentTarget
    const form = btn.closest("form")
    if (!form) return

    // Recreate the POST-with-form-values that a type="submit" Back button would do,
    // but from a type="button" so that Enter-key never accidentally triggers Back.
    const temp = document.createElement("button")
    temp.type = "submit"
    temp.formAction = btn.dataset.formaction
    temp.formMethod = "post"
    temp.formNoValidate = true
    temp.dataset.turboFrame = btn.dataset.turboFrame
    temp.style.display = "none"
    form.appendChild(temp)
    form.requestSubmit(temp)
    temp.remove()
  }

  channelValueChanged() {
    this.updateChannelUI()
  }

  updateCardState(card, selected) {
    if (!card) return
    card.classList.toggle("border-blue-500", selected)
    card.classList.toggle("bg-blue-500/10", selected)
    card.classList.toggle("border-gray-600", !selected)
    card.classList.toggle("dark:border-gray-700", !selected)

    const radio = card.querySelector("[data-radio-indicator]")
    if (!radio) return
    radio.classList.toggle("border-blue-500", selected)
    radio.classList.toggle("border-gray-500", !selected)

    let dot = radio.querySelector("[data-dot]")
    if (selected && !dot) {
      dot = document.createElement("div")
      dot.className = "w-2.5 h-2.5 rounded-full bg-blue-500"
      dot.dataset.dot = ""
      radio.appendChild(dot)
    } else if (!selected && dot) {
      dot.remove()
    }
  }

  updateChannelUI() {
    const isSms = this.channelValue === "sms"

    if (this.hasPhoneFieldTarget) {
      this.phoneFieldTarget.classList.toggle("hidden", !isSms)
      const phoneInput = this.phoneFieldTarget.querySelector("input")
      if (phoneInput) phoneInput.toggleAttribute("required", isSms)
    }

    if (this.hasEmailFieldTarget) {
      this.emailFieldTarget.classList.toggle("hidden", isSms)
      const emailInput = this.emailFieldTarget.querySelector("input")
      if (emailInput) emailInput.toggleAttribute("required", !isSms)
    }

    if (this.hasSmsConsentTarget) this.smsConsentTarget.classList.toggle("hidden", !isSms)
    if (this.hasEmailConsentTarget) this.emailConsentTarget.classList.toggle("hidden", isSms)
    if (this.hasSmsConsentCheckboxTarget) this.smsConsentCheckboxTarget.toggleAttribute("required", isSms)
    if (this.hasPhoneTrustTarget) this.phoneTrustTarget.classList.toggle("hidden", !isSms)
    this.updateSmsConsentUI()
    if (this.hasEmailTrustTarget) this.emailTrustTarget.classList.toggle("hidden", isSms)

    const contactMethodInput = this.element.querySelector("input[name*='[contact_method]']")
    if (contactMethodInput) contactMethodInput.value = this.channelValue

    this.updateCardState(this.hasSmsCardTarget ? this.smsCardTarget : null, isSms)
    this.updateCardState(this.hasEmailCardTarget ? this.emailCardTarget : null, !isSms)
  }

  updateSmsConsentUI() {
    const isSms = this.channelValue === "sms"
    const isChecked = this.hasSmsConsentCheckboxTarget && this.smsConsentCheckboxTarget.checked
    const needsConsent = isSms && !isChecked

    // Target the Send Message button (not the Back button which has formaction set)
    const submitBtn = this.element.querySelector("button[type='submit']:not([formaction])")
    if (submitBtn) submitBtn.disabled = needsConsent

    // Only ever hide the error here — it is shown only when the user taps the disabled button
    if (!needsConsent && this.hasSmsConsentErrorTarget) this.smsConsentErrorTarget.classList.add("hidden")
  }
}
